import { ChevronLeft, ChevronRight } from "lucide-react";
import classNames from "classnames";

import { useLanguage } from "../../../context/LanguageProvider";
import { toFarsiNumber } from "../../utils/commonUtils";
import { getPageItems } from "../../utils/pagination";

// Shared look for every pager button
const BASE_BUTTON =
    "flex items-center justify-center min-w-10 h-10 px-2 rounded-xl text-lg " +
    "select-none transition-all duration-150 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500";

// Same raised look as the site's Button components
const ACTIVE_BUTTON = "bg-[#4d4d4d] text-white shadow-[0_3px_0_#2d2d2d] -translate-y-px";

const IDLE_BUTTON =
    "bg-white border-2 border-gray-200 text-gray-700 " +
    "hover:border-gray-400 hover:bg-gray-50 active:translate-y-px";

const DISABLED_BUTTON = "opacity-40 cursor-not-allowed";

interface PaginationProps {
    page: number; // Current page, 1-based
    totalPages: number;
    onPageChange: (page: number) => void; // Receives a 1-based page
    siblingCount?: number;
    className?: string;
}

const Pagination = ({
                        page,
                        totalPages,
                        onPageChange,
                        siblingCount = 1,
                        className
                    }: PaginationProps) => {
    const { lang, t } = useLanguage();

    // Nothing to paginate
    if (totalPages <= 1) return null;

    // Persian digits in the Persian UI
    const formatNumber = (value: number) => (lang === "fa" ? toFarsiNumber(value) : String(value));

    const isFirst = page <= 1;
    const isLast = page >= totalPages;

    return (
        <nav
            aria-label={t.utils.pagination}
            className={classNames("flex justify-center mt-10", className)}
        >
            <ul className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 list-none p-0 m-0">
                {/* Previous. The chevron flips in RTL, so it always points the reading-backwards way */}
                <li>
                    <button
                        type="button"
                        onClick={() => onPageChange(page - 1)}
                        disabled={isFirst}
                        aria-label={t.utils.before}
                        className={classNames(BASE_BUTTON, IDLE_BUTTON, isFirst && DISABLED_BUTTON)}
                    >
                        <ChevronLeft size={20} className="rtl:rotate-180" aria-hidden="true" />
                    </button>
                </li>

                {getPageItems(page, totalPages, siblingCount).map((item) => {
                    if (typeof item === "string") {
                        return (
                            <li
                                key={item}
                                aria-hidden="true"
                                className="min-w-6 text-center text-gray-400 select-none"
                            >
                                …
                            </li>
                        );
                    }

                    const isActive = item === page;

                    return (
                        <li key={item}>
                            <button
                                type="button"
                                onClick={() => onPageChange(item)}
                                aria-label={`${t.utils.page} ${formatNumber(item)}`}
                                aria-current={isActive ? "page" : undefined}
                                className={classNames(
                                    BASE_BUTTON,
                                    isActive ? ACTIVE_BUTTON : IDLE_BUTTON
                                )}
                            >
                                {formatNumber(item)}
                            </button>
                        </li>
                    );
                })}

                {/* Next */}
                <li>
                    <button
                        type="button"
                        onClick={() => onPageChange(page + 1)}
                        disabled={isLast}
                        aria-label={t.utils.after}
                        className={classNames(BASE_BUTTON, IDLE_BUTTON, isLast && DISABLED_BUTTON)}
                    >
                        <ChevronRight size={20} className="rtl:rotate-180" aria-hidden="true" />
                    </button>
                </li>
            </ul>
        </nav>
    );
};

export default Pagination;